import React, { useState, useEffect } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import Button from '../../../shared/components/ui/Button';
// import { playersService } from '../../../shared/services/players'; // Removed unused import
import StatusConfirmModal from '../../../shared/components/ui/StatusConfirmModal';
import EditKeyEventModal from '../components/EditKeyEventModal';
import { eventsCache, createCacheKey } from '../../../shared/utils/cacheUtils';
import { getStatIcon } from '../../../shared/utils/statIconUtils';

interface MatchDetails {
  id: string;
  homeTeam: {
    id: string;
    name: string;
    logo: string;
    score: number;
  };
  awayTeam: {
    id: string;
    name: string;
    logo: string;
    score: number;
  };
}

interface KeyEventsTabProps {
  matchDetails: MatchDetails;
  isActive?: boolean;
}

interface Player {
  id: string;
  full_name: string;
  kit_number?: number;
  position?: string;
}

interface KeyEvent {
  id: string;
  match_id: string;
  team_id: string | null;
  stat_type_id: string;
  match_minute: number | null; // Made nullable until timer issue is fixed
  primary_player_id: string | null;
  secondary_player_id: string | null;
  tags: any[];
  status: 'published' | 'unpublished';
  published_at: string | null;
  published_by: string | null;
  source_log_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  event_timestamp: string | null;
  is_key_event?: boolean; // For timeline events - indicates if this is also a key event
  action?: 'increment' | 'decrement'; // For timeline events - indicates increment or decrement
  
  // Joined data
  team?: {
    id: string;
    name: string;
    logo?: string;
  } | null;
  primary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  secondary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  stat_type?: {
    id: string;
    stat_key: string;
    display_name: string;
    category: string;
    event_result: string;
  } | null;
}

const KeyEventsTab: React.FC<KeyEventsTabProps> = ({ matchDetails, isActive = true }) => {
  // Debug: Log the match details received
  console.log('🚀 KeyEventsTab - matchDetails received:', matchDetails);
  console.log('🚀 KeyEventsTab - matchDetails.id:', matchDetails.id);
  console.log('🚀 KeyEventsTab - matchDetails.id type:', typeof matchDetails.id);
  console.log('🚀 KeyEventsTab - isActive:', isActive);
  console.log('🚀 KeyEventsTab - Component mounted!');

  const [events, setEvents] = useState<KeyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [statTypes, setStatTypes] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<KeyEvent>>({});
  const [viewMode, setViewMode] = useState<'keyEvents' | 'allEvents'>('keyEvents');
  
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState<KeyEvent | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('edit');
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [selectedEventForPublish, setSelectedEventForPublish] = useState<KeyEvent | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEventForDelete, setSelectedEventForDelete] = useState<KeyEvent | null>(null);

  // Function to fetch stat types
  const fetchStatTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('stat_types')
        .select('*')
        .order('display_name');

      if (error) {
        throw new Error(`Failed to fetch stat types: ${error.message}`);
      }

      setStatTypes(data || []);
    } catch (err) {
      console.error('Error fetching stat types:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stat types');
    }
  };

  // Function to fetch teams for this match
  const fetchTeams = async () => {
    try {
      const teamsData = [
        {
          id: matchDetails.homeTeam.id,
          name: matchDetails.homeTeam.name,
          logo: matchDetails.homeTeam.logo
        },
        {
          id: matchDetails.awayTeam.id,
          name: matchDetails.awayTeam.name,
          logo: matchDetails.awayTeam.logo
        }
      ];
      setTeams(teamsData);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch teams');
    }
  };

  // Function to fetch key events - only published events
  const fetchKeyEvents = async (isInitialLoad: boolean = false) => {
    if (!matchDetails.id) return;
    
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      console.log('Fetching key events for match:', matchDetails.id);
      
      // Optimized: Select only needed fields instead of * to reduce egress
      // Note: is_key_event doesn't exist in key_match_events table - all events here are key events
      const { data, error } = await supabase
        .from('key_match_events')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          match_minute,
          primary_player_id,
          secondary_player_id,
          tags,
          status,
          published_at,
          published_by,
          source_log_id,
          is_active,
          created_at,
          updated_at,
          created_by,
          event_timestamp,
          team:teams(id, name, logo),
          primary_player:players!primary_player_id(id, full_name, kit_number),
          secondary_player:players!secondary_player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchDetails.id)
        .order('event_timestamp', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch key events: ${error.message}`);
      }

      console.log('Key events fetched successfully:', data?.length || 0);
      
      // Transform to KeyEvent format - ensure joined data is single objects, not arrays
      const transformedEvents: KeyEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        match_id: event.match_id,
        team_id: event.team_id,
        stat_type_id: event.stat_type_id,
        match_minute: event.match_minute,
        primary_player_id: event.primary_player_id,
        secondary_player_id: event.secondary_player_id,
        tags: event.tags || [],
        status: event.status,
        published_at: event.published_at,
        published_by: event.published_by,
        source_log_id: event.source_log_id,
        is_active: event.is_active,
        created_at: event.created_at,
        updated_at: event.updated_at,
        created_by: event.created_by,
        event_timestamp: event.event_timestamp,
        is_key_event: true, // All events from key_match_events are key events
        // Ensure joined data is single objects, not arrays
        team: Array.isArray(event.team) ? (event.team[0] || null) : (event.team || null),
        primary_player: Array.isArray(event.primary_player) 
          ? (event.primary_player[0] || null) 
          : (event.primary_player || null),
        secondary_player: Array.isArray(event.secondary_player) 
          ? (event.secondary_player[0] || null) 
          : (event.secondary_player || null),
        stat_type: Array.isArray(event.stat_type) 
          ? (event.stat_type[0] || null) 
          : (event.stat_type || null),
      }));
      
      const getSortWeight = (event: KeyEvent) => {
        const statKey = event.stat_type?.stat_key;
        // Ensure terminal events (full time, etc.) bubble to the top even if added later
        return statKey === 'full_time' ? -1 : 0;
      };

      // Custom sorting: prioritize match_minute when available, otherwise use event_timestamp
      const sortedEvents = transformedEvents.sort((a, b) => {
        const weightDiff = getSortWeight(a) - getSortWeight(b);
        if (weightDiff !== 0) {
          return weightDiff;
        }

        if (a.match_minute !== null && b.match_minute !== null) {
          return b.match_minute - a.match_minute;
        }
        if (a.match_minute !== null && b.match_minute === null) {
          return -1;
        }
        if (a.match_minute === null && b.match_minute !== null) {
          return 1;
        }
        const timeA = new Date(a.event_timestamp || a.created_at).getTime();
        const timeB = new Date(b.event_timestamp || b.created_at).getTime();
        return timeB - timeA;
      });
      
      setEvents(sortedEvents);
    } catch (err) {
      console.error('Error fetching key events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch key events');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Function to fetch all timeline events
  const fetchTimelineEvents = async (isInitialLoad: boolean = false) => {
    if (!matchDetails.id) return;
    
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      // Check cache first to reduce egress
      const cacheKey = createCacheKey('timeline_events', matchDetails.id);
      const cachedEvents = eventsCache.get(cacheKey) as KeyEvent[] | null;
      
      if (cachedEvents) {
        console.log('📦 Using cached timeline events for match:', matchDetails.id);
        setEvents(cachedEvents);
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setIsRefreshing(false);
        }
        return;
      }
      
      console.log('🔄 Fetching timeline events for match:', matchDetails.id);
      
      // Optimized: Select only needed fields instead of * to reduce egress
      const { data, error } = await supabase
        .from('match_timeline')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          match_minute,
          player_id,
          tags,
          is_key_event,
          source_log_id,
          is_active,
          action,
          created_at,
          updated_at,
          event_timestamp,
          team:teams(id, name, logo),
          player:players!player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchDetails.id)
        .order('event_timestamp', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch timeline events: ${error.message}`);
      }

      console.log('Timeline events fetched successfully:', data?.length || 0);
      
      // Get actual key events from key_match_events table to check which timeline events are truly key events
      const { data: actualKeyEvents } = await supabase
        .from('key_match_events')
        .select('source_log_id, status')
        .eq('match_id', matchDetails.id);
      
      // Create maps for quick lookup
      const keyEventSourceLogIds = new Set<string>();
      const keyEventStatuses: Record<string, 'published' | 'unpublished'> = {};
      
      if (actualKeyEvents) {
        actualKeyEvents.forEach((ke: any) => {
          if (ke.source_log_id) {
            keyEventSourceLogIds.add(ke.source_log_id);
            keyEventStatuses[ke.source_log_id] = ke.status as 'published' | 'unpublished';
          }
        });
      }
      
      // Transform timeline events to KeyEvent format
      const transformedEvents: KeyEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        match_id: event.match_id,
        team_id: event.team_id,
        stat_type_id: event.stat_type_id,
        match_minute: event.match_minute,
        primary_player_id: event.player_id || null, // Map player_id to primary_player_id
        secondary_player_id: null,
        tags: event.tags || [],
        status: event.source_log_id && keyEventStatuses[event.source_log_id]
          ? keyEventStatuses[event.source_log_id]
          : 'unpublished', // Fetch status from key_match_events if it's a key event
        published_at: null,
        published_by: null,
        source_log_id: event.source_log_id,
        is_active: event.is_active,
        created_at: event.created_at,
        updated_at: event.updated_at,
        created_by: null,
        event_timestamp: event.event_timestamp,
        is_key_event: event.source_log_id ? keyEventSourceLogIds.has(event.source_log_id) : false, // Check if actually exists in key_match_events
        action: event.action || 'increment', // Include action field (increment/decrement)
        // Joined data - ensure single objects, not arrays
        team: Array.isArray(event.team) ? (event.team[0] || null) : (event.team || null),
        primary_player: Array.isArray(event.player) 
          ? (event.player[0] ? {
              id: event.player[0].id,
              full_name: event.player[0].full_name,
              kit_number: event.player[0].kit_number
            } : null)
          : (event.player ? {
              id: event.player.id,
              full_name: event.player.full_name,
              kit_number: event.player.kit_number
            } : null),
        secondary_player: null,
        stat_type: Array.isArray(event.stat_type) 
          ? (event.stat_type[0] || null) 
          : (event.stat_type || null)
      }));
      
      const getSortWeight = (event: KeyEvent) => {
        const statKey = event.stat_type?.stat_key;
        return statKey === 'full_time' ? -1 : 0;
      };

      // Custom sorting: prioritize match_minute when available, otherwise use event_timestamp
      const sortedEvents = transformedEvents.sort((a, b) => {
        const weightDiff = getSortWeight(a) - getSortWeight(b);
        if (weightDiff !== 0) {
          return weightDiff;
        }

        if (a.match_minute !== null && b.match_minute !== null) {
          return b.match_minute - a.match_minute;
        }
        if (a.match_minute !== null && b.match_minute === null) {
          return -1;
        }
        if (a.match_minute === null && b.match_minute !== null) {
          return 1;
        }
        const timeA = new Date(a.event_timestamp || a.created_at).getTime();
        const timeB = new Date(b.event_timestamp || b.created_at).getTime();
        return timeB - timeA;
      });
      
      // Cache the sorted events to reduce future egress (30 second TTL)
      eventsCache.set(cacheKey, sortedEvents);
      console.log('💾 Cached timeline events for match:', matchDetails.id);
      
      setEvents(sortedEvents);
    } catch (err) {
      console.error('Error fetching timeline events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch timeline events');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Unified fetch function based on view mode
  const fetchEvents = async () => {
    if (viewMode === 'keyEvents') {
      await fetchKeyEvents();
    } else {
      await fetchTimelineEvents();
    }
  };

  // Fetch players for selection from match lineup
  const fetchPlayers = async () => {
    try {
      console.log('🔥 === FETCH PLAYERS DEBUG ===');
      console.log('🔥 matchDetails object:', matchDetails);
      console.log('🔥 matchDetails.id:', matchDetails.id);
      console.log('🔥 matchDetails.id type:', typeof matchDetails.id);
      console.log('🔥 matchDetails.id length:', matchDetails.id?.length);
      
      if (!matchDetails.id) {
        console.error('No match ID found! Cannot fetch players.');
        return;
      }
      
      console.log('Fetching players for match_id:', matchDetails.id);
      
      // First, let's check if match_lineup table exists and has data
      const { data: lineupData, error: lineupError } = await supabase
        .from('match_lineup')
        .select(`
          player_id,
          players!inner(
            id,
            full_name,
            kit_number
          )
        `)
        .eq('match_id', matchDetails.id);

      console.log('Match lineup query result:', { lineupData, lineupError });

      if (lineupError) {
        console.log('Match lineup table error, trying fallback approach...');
        
        // Fallback: Get teams from match and fetch players directly
        const { data: matchData, error: matchError } = await supabase
          .from('matches')
          .select('home_team_id, away_team_id')
          .eq('id', matchDetails.id)
          .single();

        console.log('Match data for fallback:', { matchData, matchError });

        if (matchError || !matchData) {
          throw new Error(`Failed to fetch match data: ${matchError?.message}`);
        }

        // Get players from both teams
        const { data: playersData, error: playersError } = await supabase
          .from('players')
          .select('id, full_name, kit_number')
          .in('team_id', [matchData.home_team_id, matchData.away_team_id]);

        console.log('Fallback players query result:', { playersData, playersError });

        if (playersError) {
          throw new Error(`Failed to fetch players: ${playersError.message}`);
        }

        const transformedPlayers = playersData?.map(player => ({
          id: player.id,
          full_name: player.full_name,
          kit_number: player.kit_number,
          team_id: null
        })) || [];

        console.log('Final players data (fallback):', transformedPlayers);
        setPlayers(transformedPlayers);
        return;
      }

      // Transform the data to match our Player interface
      const playersData = lineupData?.map((lineup: any) => ({
        id: lineup.players?.id,
        full_name: lineup.players?.full_name,
        kit_number: lineup.players?.kit_number,
        team_id: null // We don't need team_id for the dropdown
      })) || [];

      console.log('Final players data (from lineup):', playersData);
      setPlayers(playersData);
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  // Fetch events when component mounts, tab is active, or view mode changes
  useEffect(() => {
    if (isActive && matchDetails.id) {
      const isInitialLoad = events.length === 0;
      if (viewMode === 'keyEvents') {
        fetchKeyEvents(isInitialLoad);
      } else {
        fetchTimelineEvents(isInitialLoad);
      }
      fetchPlayers();
      fetchStatTypes();
      fetchTeams();
    }
  }, [isActive, matchDetails.id, viewMode]);

  // Expose create new handler to parent component via window
  useEffect(() => {
    if (isActive) {
      (window as any).keyEventsCreateHandler = handleCreateNew;
      (window as any).keyEventsPublishAllHandler = handlePublishAll;
    }
    return () => {
      delete (window as any).keyEventsCreateHandler;
      delete (window as any).keyEventsPublishAllHandler;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Update event function
  const updateEvent = async (eventId: string, updates: Partial<KeyEvent> & { is_key_event?: boolean }) => {
    try {
      // Prepare the update data with proper null handling
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      // Handle each field properly
      if (updates.match_minute !== undefined) {
        updateData.match_minute = updates.match_minute;
      }
      if (updates.team_id !== undefined) {
        updateData.team_id = updates.team_id || null;
      }
      if (updates.primary_player_id !== undefined) {
        updateData.primary_player_id = updates.primary_player_id || null;
      }
      if (updates.secondary_player_id !== undefined) {
        updateData.secondary_player_id = updates.secondary_player_id || null;
      }
      if (updates.status !== undefined) {
        updateData.status = updates.status;
      }
      if (updates.stat_type_id !== undefined) {
        updateData.stat_type_id = updates.stat_type_id;
      }
      if (updates.is_active !== undefined) {
        updateData.is_active = updates.is_active;
      }

      console.log('Updating event with data:', updateData, 'viewMode:', viewMode);

      // Update based on view mode
      if (viewMode === 'keyEvents') {
        // If is_active is being set to false, also set status to unpublished
        if (updates.is_active === false && !updateData.status) {
          updateData.status = 'unpublished';
        }
        
        // Updating key_match_events
      const { error } = await supabase
        .from('key_match_events')
        .update(updateData)
        .eq('id', eventId);

      if (error) {
          throw new Error(`Failed to update key event: ${error.message}`);
        }

        // Also update match_timeline if it exists (find by source_log_id)
        // This ensures both tables stay in sync
        const { data: keyEvent } = await supabase
          .from('key_match_events')
          .select('source_log_id')
          .eq('id', eventId)
          .single();

        if (keyEvent?.source_log_id) {
          const timelineUpdateData: any = {
            updated_at: new Date().toISOString(),
            is_key_event: true // Key events are always key events
          };
          if (updates.match_minute !== undefined) {
            timelineUpdateData.match_minute = updates.match_minute;
          }
          if (updates.team_id !== undefined) {
            timelineUpdateData.team_id = updates.team_id || null;
          }
          if (updates.primary_player_id !== undefined) {
            timelineUpdateData.player_id = updates.primary_player_id || null;
          }
          if (updates.stat_type_id !== undefined) {
            timelineUpdateData.stat_type_id = updates.stat_type_id;
          }
          if (updates.is_active !== undefined) {
            timelineUpdateData.is_active = updates.is_active;
          }

          // Update match_timeline entry if it exists to keep both tables in sync
          const { error: timelineError } = await supabase
            .from('match_timeline')
            .update(timelineUpdateData)
            .eq('source_log_id', keyEvent.source_log_id);

          // Propagate is_active to match_visualization and match_video_tags
          if (updates.is_active !== undefined && keyEvent.source_log_id) {
            await supabase
              .from('match_visualization')
              .update({ 
                is_active: updates.is_active,
                updated_at: new Date().toISOString()
              })
              .eq('source_log_id', keyEvent.source_log_id);

            await supabase
              .from('match_video_tags')
              .update({ 
                is_active: updates.is_active,
                updated_at: new Date().toISOString()
              })
              .eq('source_log_id', keyEvent.source_log_id);
          }

          if (timelineError) {
            console.warn('Failed to update timeline event:', timelineError);
            // Don't throw - this is a secondary update
          }
        }
      } else {
        // Updating match_timeline
        // First, get the current timeline event to check if it's also a key event
        const { data: timelineEvent } = await supabase
          .from('match_timeline')
          .select('*')
          .eq('id', eventId)
          .single();

        if (!timelineEvent) {
          throw new Error('Timeline event not found');
        }

        // Prepare timeline update data
        const timelineUpdateData: any = {
          updated_at: new Date().toISOString()
        };
        if (updates.match_minute !== undefined) {
          timelineUpdateData.match_minute = updates.match_minute;
        }
        if (updates.team_id !== undefined) {
          timelineUpdateData.team_id = updates.team_id || null;
        }
        if (updates.primary_player_id !== undefined) {
          timelineUpdateData.player_id = updates.primary_player_id || null;
        }
        if (updates.stat_type_id !== undefined) {
          timelineUpdateData.stat_type_id = updates.stat_type_id;
        }
        if (updates.is_key_event !== undefined) {
          timelineUpdateData.is_key_event = updates.is_key_event;
        }
        if (updates.is_active !== undefined) {
          timelineUpdateData.is_active = updates.is_active;
        }

        // Update match_timeline
        const { error: timelineError } = await supabase
          .from('match_timeline')
          .update(timelineUpdateData)
          .eq('id', eventId);

        if (timelineError) {
          throw new Error(`Failed to update timeline event: ${timelineError.message}`);
        }

        // Check if a corresponding key event exists (by source_log_id)
        const { data: existingKeyEvent } = await supabase
          .from('key_match_events')
          .select('id')
          .eq('source_log_id', timelineEvent.source_log_id)
          .single();

        const isKeyEvent = updates.is_key_event !== undefined 
          ? updates.is_key_event 
          : timelineEvent.is_key_event;

        if (existingKeyEvent) {
          // Key event exists - always sync updates to keep both tables in sync
          const keyEventUpdateData: any = {
            updated_at: new Date().toISOString()
          };
          if (updates.match_minute !== undefined) {
            keyEventUpdateData.match_minute = updates.match_minute;
          }
          if (updates.team_id !== undefined) {
            keyEventUpdateData.team_id = updates.team_id || null;
          }
          if (updates.primary_player_id !== undefined) {
            keyEventUpdateData.primary_player_id = updates.primary_player_id || null;
          }
          if (updates.secondary_player_id !== undefined) {
            keyEventUpdateData.secondary_player_id = updates.secondary_player_id || null;
          }
          if (updates.stat_type_id !== undefined) {
            keyEventUpdateData.stat_type_id = updates.stat_type_id;
          }
          if (updates.status !== undefined) {
            keyEventUpdateData.status = updates.status;
          }
          if (updates.is_active !== undefined) {
            keyEventUpdateData.is_active = updates.is_active;
            // If deactivating, also set status to unpublished
            if (updates.is_active === false && !keyEventUpdateData.status) {
              keyEventUpdateData.status = 'unpublished';
            }
          }

          // If is_key_event is being set to false, delete the key event
          if (updates.is_key_event === false) {
            const { error: deleteError } = await supabase
              .from('key_match_events')
              .delete()
              .eq('id', existingKeyEvent.id);

            if (deleteError) {
              console.warn('Failed to delete key event:', deleteError);
            }
          } else {
            // Update the existing key event to keep it in sync
            const { error: keyEventError } = await supabase
              .from('key_match_events')
              .update(keyEventUpdateData)
              .eq('id', existingKeyEvent.id);

            if (keyEventError) {
              console.warn('Failed to update key event:', keyEventError);
            }
          }

          // Propagate is_active to match_visualization and match_video_tags
          if (updates.is_active !== undefined && timelineEvent.source_log_id) {
            await supabase
              .from('match_visualization')
              .update({ 
                is_active: updates.is_active,
                updated_at: new Date().toISOString()
              })
              .eq('source_log_id', timelineEvent.source_log_id);

            await supabase
              .from('match_video_tags')
              .update({ 
                is_active: updates.is_active,
                updated_at: new Date().toISOString()
              })
              .eq('source_log_id', timelineEvent.source_log_id);
          }
        } else if (isKeyEvent === true) {
          // No key event exists but is_key_event is true - create new key event
          const { data: updatedTimelineEvent } = await supabase
            .from('match_timeline')
            .select('*')
            .eq('id', eventId)
            .single();

          if (updatedTimelineEvent) {
            const { data: { user } } = await supabase.auth.getUser();
            const { error: keyEventError } = await supabase
              .from('key_match_events')
              .insert({
                match_id: updatedTimelineEvent.match_id,
                team_id: updatedTimelineEvent.team_id,
                stat_type_id: updatedTimelineEvent.stat_type_id,
                match_minute: updatedTimelineEvent.match_minute,
                primary_player_id: updatedTimelineEvent.player_id || null,
                secondary_player_id: updates.secondary_player_id || null,
                tags: updatedTimelineEvent.tags || [],
                status: updates.status || 'unpublished',
                source_log_id: updatedTimelineEvent.source_log_id,
                created_by: user?.id,
                is_active: true,
                event_timestamp: updatedTimelineEvent.event_timestamp,
                position_start: updatedTimelineEvent.position_start,
                position_end: updatedTimelineEvent.position_end
              });

            if (keyEventError) {
              console.warn('Failed to create key event:', keyEventError);
            }
          }
        }
      }

      // Update local state with proper joined data handling
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          const updatedEvent = { ...event, ...updates };
          
          // Update joined player data if player IDs changed
          if (updates.primary_player_id !== undefined) {
            if (updates.primary_player_id) {
              const player = players.find(p => p.id === updates.primary_player_id);
              updatedEvent.primary_player = player ? {
                id: player.id,
                full_name: player.full_name,
                kit_number: player.kit_number
              } : null;
            } else {
              updatedEvent.primary_player = null;
            }
          }
          
          if (updates.secondary_player_id !== undefined) {
            if (updates.secondary_player_id) {
              const player = players.find(p => p.id === updates.secondary_player_id);
              updatedEvent.secondary_player = player ? {
                id: player.id,
                full_name: player.full_name,
                kit_number: player.kit_number
              } : null;
            } else {
              updatedEvent.secondary_player = null;
            }
          }
          
          return updatedEvent;
        }
        return event;
      }));

      // Invalidate cache when event is updated
      const cacheKey = createCacheKey('timeline_events', matchDetails.id);
      eventsCache.delete(cacheKey);
      console.log('🗑️ Cache invalidated after event update');

      setEditingEvent(null);
      setEditingData({});
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err instanceof Error ? err.message : 'Failed to update event');
    }
  };

  const handleEditClick = (event: KeyEvent) => {
    setSelectedEventForEdit(event);
    setModalMode('edit');
    setIsEditModalOpen(true);
  };

  const handleViewClick = (event: KeyEvent) => {
    setSelectedEventForEdit(event);
    setModalMode('view');
    setIsEditModalOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedEventForEdit(null);
    setModalMode('create');
    setIsEditModalOpen(true);
  };

  const handlePublishClick = (event: KeyEvent) => {
    setSelectedEventForPublish(event);
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = async () => {
    if (selectedEventForPublish) {
      const newStatus = selectedEventForPublish.status === 'published' ? 'unpublished' : 'published';
      await updateEvent(selectedEventForPublish.id, { status: newStatus });
      setIsPublishModalOpen(false);
      setSelectedEventForPublish(null);
    }
  };

  const handleDeleteClick = (event: KeyEvent) => {
    setSelectedEventForDelete(event);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedEventForDelete) {
      try {
        // Toggle is_active instead of deleting
        const tableName = viewMode === 'keyEvents' ? 'key_match_events' : 'match_timeline';
        const newIsActive = !selectedEventForDelete.is_active;
        const updateData: any = { 
          is_active: newIsActive,
          updated_at: new Date().toISOString()
        };
        
        // If deactivating a key event, also set status to unpublished
        if (viewMode === 'keyEvents' && !newIsActive) {
          updateData.status = 'unpublished';
        }
        
        const { error } = await supabase
          .from(tableName)
          .update(updateData)
          .eq('id', selectedEventForDelete.id);

        if (error) {
          throw new Error(`Failed to ${newIsActive ? 'activate' : 'deactivate'} event: ${error.message}`);
        }

        // Propagate is_active change to related tables via source_log_id
        if (selectedEventForDelete.source_log_id) {
          // Update match_timeline if updating key_match_events
          if (viewMode === 'keyEvents') {
            await supabase
              .from('match_timeline')
              .update({ 
                is_active: newIsActive,
                updated_at: new Date().toISOString()
              })
              .eq('source_log_id', selectedEventForDelete.source_log_id);
          }
          // Update key_match_events if updating match_timeline
          else {
            const keyEventUpdateData: any = { 
              is_active: newIsActive,
              updated_at: new Date().toISOString()
            };
            // If deactivating, also set status to unpublished
            if (!newIsActive) {
              keyEventUpdateData.status = 'unpublished';
            }
            await supabase
              .from('key_match_events')
              .update(keyEventUpdateData)
              .eq('source_log_id', selectedEventForDelete.source_log_id);
          }

          // Update match_visualization
          await supabase
            .from('match_visualization')
            .update({ 
              is_active: newIsActive,
              updated_at: new Date().toISOString()
            })
            .eq('source_log_id', selectedEventForDelete.source_log_id);

          // Update match_video_tags
          await supabase
            .from('match_video_tags')
            .update({ 
              is_active: newIsActive,
              updated_at: new Date().toISOString()
            })
            .eq('source_log_id', selectedEventForDelete.source_log_id);
        }

        // Optimistically update local state for immediate UI feedback
        setEvents(prev => prev.map(event => 
          event.id === selectedEventForDelete.id
            ? { ...event, is_active: newIsActive }
            : event
        ));

        // Invalidate cache before refreshing to ensure fresh data
        const cacheKey = createCacheKey('timeline_events', matchDetails.id);
        eventsCache.delete(cacheKey);
        console.log('🗑️ Cache invalidated after event delete');

        // Refresh events to show updated status (this will confirm the optimistic update)
        await fetchEvents();
        setIsDeleteModalOpen(false);
        setSelectedEventForDelete(null);
      } catch (error) {
        console.error('Error toggling event active status:', error);
        throw error;
      }
    }
  };

  // Publish all key events for this match
  const handlePublishAll = async () => {
    try {
      if (!matchDetails.id) {
        throw new Error('Match ID is required');
      }

      // Update all key_match_events for this match to published
      const { error } = await supabase
        .from('key_match_events')
        .update({
          status: 'published',
          updated_at: new Date().toISOString()
        })
        .eq('match_id', matchDetails.id)
        .eq('is_active', true);

      if (error) {
        throw new Error(`Failed to publish all events: ${error.message}`);
      }

      // Refresh events to show updated status
      await fetchEvents();
    } catch (error) {
      console.error('Error publishing all events:', error);
      throw error;
    }
  };

  const createNewEvent = async (eventData: Partial<KeyEvent> & { is_key_event?: boolean }) => {
    try {
      if (!eventData.stat_type_id) {
        throw new Error('Event type is required');
      }
      if (!eventData.team_id) {
        throw new Error('Team is required');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Get stat type info for generating tags
      const selectedStatType = statTypes.find(st => st.id === eventData.stat_type_id);
      if (!selectedStatType) {
        throw new Error('Stat type not found');
      }

      // Generate tags based on stat type
      const tags: any[] = [];
      tags.push(selectedStatType.stat_key);
      if (selectedStatType.category) {
        tags.push(selectedStatType.category);
      }
      if (selectedStatType.event_result) {
        tags.push(selectedStatType.event_result);
      }

      // Determine if this is a key event stat type
      const isKeyEventStatType = [
        'shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored', 'penalty_missed',
        'penalty_shootout_scored', 'penalty_shootout_missed', 'own_goal', 'assist',
        'yellow_card', 'red_card', 'substitution',
        'save_open_play', 'save_penalty', 'penalty_punch', 'penalty_claim',
        'open_play_punch', 'open_play_claim',
        'kick_off', 'end_1st_half', 'start_2nd_half', 'start_extra_time_1st_half',
        'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout', 'full_time'
      ].includes(selectedStatType.stat_key);

      // Determine final is_key_event value
      const isKeyEvent = eventData.is_key_event || isKeyEventStatType;

      // Create a minimal scoresheet_log entry for manual entries (required for source_log_id)
      const { data: manualLog, error: logError } = await supabase
        .from('scoresheet_logs')
        .insert({
          match_id: matchDetails.id,
          team_id: eventData.team_id || null,
          stat_type_id: eventData.stat_type_id,
          player_id: eventData.primary_player_id || null,
          action: 'increment',
          previous_value: 0,
          new_value: 1,
          match_minute: eventData.match_minute || null,
          timestamp: new Date().toISOString(),
          user_id: user?.id || null,
          is_active: true,
          position_data: null // Manual entry, no position data
        })
        .select()
        .single();

      if (logError) {
        throw new Error(`Failed to create log entry: ${logError.message}`);
      }

      // Always create match_timeline row
      const { error: timelineError } = await supabase
        .from('match_timeline')
        .insert({
          match_id: matchDetails.id,
          source_log_id: manualLog.id, // Use the manual log entry ID
          stat_type_id: eventData.stat_type_id,
          team_id: eventData.team_id || null,
          player_id: eventData.primary_player_id || null,
          match_minute: eventData.match_minute || null,
          event_timestamp: new Date().toISOString(),
          position_start: null, // Manual entry, no position data
          position_end: null,
          tags: tags,
          action: 'increment',
          stat_value: 1, // Default value for manual entries
          is_key_event: isKeyEvent,
          is_active: true
        });

      if (timelineError) {
        throw new Error(`Failed to create timeline event: ${timelineError.message}`);
      }

      // Conditionally create key_match_events row if is_key_event is true
      if (isKeyEvent) {
        const { error: keyEventError } = await supabase
        .from('key_match_events')
        .insert({
          match_id: matchDetails.id,
            team_id: eventData.team_id || null,
          stat_type_id: eventData.stat_type_id,
          match_minute: eventData.match_minute || null,
          primary_player_id: eventData.primary_player_id || null,
          secondary_player_id: eventData.secondary_player_id || null,
            tags: tags,
          status: eventData.status || 'unpublished',
            source_log_id: manualLog.id, // Use the manual log entry ID
            created_by: user?.id,
          is_active: true,
            event_timestamp: new Date().toISOString(),
            position_start: null,
            position_end: null
        });

        if (keyEventError) {
          throw new Error(`Failed to create key event: ${keyEventError.message}`);
        }
      }
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  };

  // Save editing function
  const saveEditing = () => {
    if (!editingEvent) return;
    updateEvent(editingEvent, editingData);
  };

  // Cancel editing function
  const cancelEditing = () => {
    setEditingEvent(null);
    setEditingData({});
  };

  // Helper function to format time display
  const formatTimeDisplay = (event: KeyEvent) => {
    if (event.match_minute !== null && event.match_minute !== undefined) {
      return `${event.match_minute}'`;
    }
    return '--';
  };

  // Helper function to format player display
  const formatPlayerDisplay = (event: KeyEvent) => {
    const eventType = event.stat_type?.stat_key || '';
    const primaryPlayer = event.primary_player;
    const secondaryPlayer = event.secondary_player;
    
    if (!primaryPlayer && !secondaryPlayer) {
      return '';
    }
    
    // For substitution events
    if (eventType === 'substitution') {
      if (primaryPlayer && secondaryPlayer) {
        return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''} in for ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      } else if (primaryPlayer) {
        return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
      } else if (secondaryPlayer) {
        return `${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      }
    }
    
    // For goal events
    if (eventType.includes('goal')) {
      if (primaryPlayer && secondaryPlayer) {
        return `Goal by ${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''} assist by ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      } else if (primaryPlayer) {
        return `Goal by ${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
      } else if (secondaryPlayer) {
        return `Assist by ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      }
    }
    
    // For other events with players
    if (primaryPlayer && secondaryPlayer) {
      return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}, ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
    } else if (primaryPlayer) {
      return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
    } else if (secondaryPlayer) {
      return `${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
    }
    
    return '';
  };

  // Get event icon
  // Use shared utility function for icons

  // Get event color
  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'goal':
      case 'shot_inside_box_goal':
      case 'shot_outside_box_goal':
      case 'penalty_scored':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'penalty_missed':
      case 'red_card':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'yellow_card':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'substitution':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'save_open_play':
      case 'save_penalty':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'kick_off':
      case 'end_1st_half':
      case 'start_2nd_half':
      case 'full_time':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get short description for stat type
  const getShortDescription = (statKey: string) => {
    switch (statKey) {
      case 'shot_inside_box_goal':
        return 'Goal (Inside Box)';
      case 'shot_outside_box_goal':
        return 'Goal (Outside Box)';
      case 'penalty_scored':
        return 'Penalty Goal';
      case 'penalty_missed':
        return 'Penalty Missed';
      case 'yellow_card':
        return 'Yellow Card';
      case 'red_card':
        return 'Red Card';
      case 'substitution':
        return 'Substitution';
      case 'save_open_play':
        return 'Save (Open Play)';
      case 'save_penalty':
        return 'Save (Penalty)';
      case 'kick_off':
        return 'Kick Off';
      case 'end_1st_half':
        return 'End 1st Half';
      case 'start_2nd_half':
        return 'Start 2nd Half';
      case 'full_time':
        return 'Full Time';
      case 'own_goal':
        return 'Own Goal';
      case 'tackle_success':
        return 'Tackle Won';
      case 'foul_own_half':
        return 'Foul (Own Half)';
      case 'foul_opp_half':
        return 'Foul (Opp Half)';
      default:
        return statKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Helper function to check if an event is a key event
  const isKeyEvent = (event: KeyEvent): boolean => {
    // In key events view, all events are key events (they come from key_match_events table)
    if (viewMode === 'keyEvents') {
      return true;
    }
    // In all events view, check if it actually exists in key_match_events (via is_key_event flag)
    return event.is_key_event === true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600 font-medium">Loading {viewMode === 'keyEvents' ? 'key events' : 'timeline events'}...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-6">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading {viewMode === 'keyEvents' ? 'Key Events' : 'Timeline Events'}</h3>
          <p className="text-gray-500 mb-6">{error}</p>
        </div>
        <Button
          onClick={fetchEvents}
          variant="secondary"
          size="md"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="space-y-4">
        {/* Header - Match Ball Possession Strip Style */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">{viewMode === 'keyEvents' ? 'KEY EVENTS:' : 'ALL EVENTS:'}</span>
                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-600">with</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">0</span>
                <span className="text-sm text-gray-600">events</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
              <button
                  onClick={() => setViewMode('keyEvents')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    viewMode === 'keyEvents'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Key Events
              </button>
              <button
                  onClick={() => setViewMode('allEvents')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    viewMode === 'allEvents'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Events
              </button>
              </div>
            </div>
          </div>
        </div>

        {/* No Events Found Message */}
        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
          <p className="text-gray-500 mb-6">
            {viewMode === 'keyEvents' 
              ? 'No key events available for this match. Events will appear here once they are synced from the scoresheet.'
              : 'No timeline events available for this match. Events will appear here once they are synced from the scoresheet.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - Match Ball Possession Strip Style */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 relative">
        {/* Subtle loading overlay when refreshing */}
        {isRefreshing && (
          <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center z-10">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">{viewMode === 'keyEvents' ? 'KEY EVENTS:' : 'ALL EVENTS:'}</span>
              <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-sm text-gray-600">with</span>
            </div>
          <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900">{events.length} events recorded</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
            <button
                onClick={() => setViewMode('keyEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'keyEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Key Events
            </button>
            <button
                onClick={() => setViewMode('allEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'allEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                All Events
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Events List - Match Score Home Screen Style */}
      <div className="space-y-1">
        {events.map((event) => {
          const isEventKeyEvent = isKeyEvent(event);
          return (
            <div
              key={event.id}
              className={`bg-white border rounded-lg hover:bg-gray-50 transition-colors duration-150 ${
                viewMode === 'allEvents' && isEventKeyEvent
                  ? 'border-blue-400 border-2 bg-blue-50'
                  : !event.is_active
                  ? 'border-gray-300 bg-gray-50 opacity-75'
                  : 'border-gray-200'
              }`}
          >
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                {/* Left Side - Event Info */}
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* Event Icon - Smaller */}
                  <div className={`w-8 h-8 rounded flex items-center justify-center text-sm ${getEventColor(event.stat_type?.stat_key || '')}`}>
                    {getStatIcon(event.stat_type?.stat_key || '')}
                  </div>
                  
                  {/* Event Details - Compact */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${!event.is_active ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {getShortDescription(event.stat_type?.stat_key || '')}
                        </span>
                      {/* Action indicator (increment/decrement) - only for timeline events */}
                      {viewMode === 'allEvents' && event.action && (
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                          event.action === 'increment' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {event.action === 'increment' ? '+' : '-'}
                        </span>
                      )}
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                        {editingEvent === event.id ? (
                          <input
                            type="number"
                            value={editingData.match_minute || event.match_minute || ''}
                            onChange={(e) => setEditingData(prev => ({ ...prev, match_minute: e.target.value ? parseInt(e.target.value) : null }))}
                            className="w-8 text-center bg-transparent border-none outline-none text-xs"
                            min="0"
                            max="130"
                          />
                        ) : (
                          formatTimeDisplay(event)
                        )}
                      </span>
                      {/* Inactive indicator */}
                      {!event.is_active && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                          inactive
                        </span>
                      )}
                      {/* Only show status badge for key events */}
                      {isEventKeyEvent && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        event.status === 'published' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {event.status}
                      </span>
                      )}
                    </div>
                    
                    {/* Event Content - Single Line */}
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-600">
                      {event.team?.name && <span>{event.team.name}</span>}
                      
                      {/* Players - Inline */}
                      {editingEvent === event.id ? (
                        <div className="flex items-center space-x-2">
                          <select
                            value={editingData.primary_player_id || event.primary_player_id || ''}
                            onChange={(e) => setEditingData(prev => ({ ...prev, primary_player_id: e.target.value || null }))}
                            className="text-xs border border-gray-300 rounded px-1 py-0.5 w-24"
                          >
                            <option value="">Primary</option>
                            {players.map(player => (
                              <option key={player.id} value={player.id}>
                                {player.full_name} {player.kit_number ? `#${player.kit_number}` : ''}
                              </option>
                            ))}
                          </select>
                          <select
                            value={editingData.secondary_player_id || event.secondary_player_id || ''}
                            onChange={(e) => setEditingData(prev => ({ ...prev, secondary_player_id: e.target.value || null }))}
                            className="text-xs border border-gray-300 rounded px-1 py-0.5 w-24"
                          >
                            <option value="">Secondary</option>
                            {players.map(player => (
                              <option key={player.id} value={player.id}>
                                {player.full_name} {player.kit_number ? `#${player.kit_number}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span>{formatPlayerDisplay(event)}</span>
                      )}
                      
                    </div>
                  </div>
                </div>

                {/* Right Side - Actions Section - Smaller Icons */}
                <div className="flex items-center space-x-1">
                  {editingEvent === event.id ? (
                    <>
                      <button
                        onClick={saveEditing}
                        className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                        title="Save"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Cancel"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Status/Publish Button - Only for key events - First position */}
                      {isEventKeyEvent && (
                        <button
                          onClick={() => handlePublishClick(event)}
                          className={`p-1.5 rounded transition-colors ${
                            event.status === 'published' 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-yellow-600 hover:bg-yellow-100'
                          }`}
                          title={event.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}
                      
                      {/* View Button - Smaller */}
                      <button
                        onClick={() => handleViewClick(event)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="View"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      
                      {/* Edit Button - Smaller */}
                      <button
                        onClick={() => handleEditClick(event)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      {/* Delete/Deactivate Button - Smaller */}
                      <button
                        onClick={() => handleDeleteClick(event)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        title={event.is_active ? "Deactivate" : "Reactivate"}
                      >
                        {event.is_active ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>
                      
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* Publish/Unpublish Confirmation Modal */}
      <StatusConfirmModal
        isOpen={isPublishModalOpen}
        onClose={() => {
          setIsPublishModalOpen(false);
          setSelectedEventForPublish(null);
        }}
        onConfirm={handleConfirmPublish}
        isPublishing={selectedEventForPublish?.status !== 'published'}
        eventTitle={selectedEventForPublish ? getShortDescription(selectedEventForPublish.stat_type?.stat_key || '') : undefined}
      />

      {/* Delete/Deactivate Confirmation Modal */}
      <StatusConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEventForDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={true}
        isActive={selectedEventForDelete?.is_active ?? true}
        eventTitle={selectedEventForDelete ? getShortDescription(selectedEventForDelete.stat_type?.stat_key || '') : undefined}
      />

      {/* Edit/View/Create Event Modal */}
      {isEditModalOpen && (
        <EditKeyEventModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedEventForEdit(null);
          }}
          event={selectedEventForEdit}
          players={players}
          teams={teams}
          statTypes={statTypes}
          isViewOnly={modalMode === 'view'}
          isCreateMode={modalMode === 'create'}
          viewMode={viewMode}
          onSave={async (updatedEvent) => {
            try {
              if (selectedEventForEdit) {
                await updateEvent(selectedEventForEdit.id, updatedEvent);
                // Refresh events after update
                await fetchEvents();
              } else {
                // Create new event
                await createNewEvent(updatedEvent);
                // Only refresh for new events since we don't have the new event data
                await fetchEvents();
              }
              setIsEditModalOpen(false);
              setSelectedEventForEdit(null);
            } catch (error) {
              console.error('Error saving event:', error);
              // Keep modal open if there's an error
            }
          }}
        />
      )}
    </div>
  );
};

export default KeyEventsTab;